// src/client/game/core/foods.ts

import { LaserCharge } from "./charges/LaserCharge";
import { TripleLaserCharge } from "./charges/TripleLaserCharge";
import { LightningCharge } from "./charges/LightningCharge";
import { SegmentSkin } from "./tail/SegmentSkin";
import { FoodKind, type FoodDefinition } from "./types";
import type { GameModel } from "./GameModel";

const MAX_LASER_LEVEL = 3;

const normalFood: FoodDefinition = {
  id: FoodKind.Normal,
  label: "Red – +10 pts, +1 length",
  color: 0xff0000,
  apply(model: GameModel): void {
    model.snake.score += 10;
    model.snake.grow(1);
  },
};

const goldFood: FoodDefinition = {
  id: FoodKind.Gold,
  label: "Gold – big growth, faster",
  color: 0xffd700,
  apply(model: GameModel): void {
    model.snake.score += 100;
    model.snake.grow(10);
    model.snake.speedMultiplier *= 1.1;
  },
};

const blueFood: FoodDefinition = {
  id: FoodKind.Blue,
  label: "Blue – slow down, shrink, spawns enemy",
  color: 0x0074d9,
  apply(model: GameModel): void {
    model.snake.score += 50;

    // Shrink player snake by half
    model.snake.shrinkHalf();

    // Decrease speed by ~25% relative to current,
    // but never let the snake become SLOWER than its starting speed.
    model.snake.speedMultiplier *= 0.75;
    if (model.snake.speedMultiplier < 1) {
      model.snake.speedMultiplier = 1;
    }

    // Spawn exactly one enemy snake.
    model.spawnEnemySnake();
  },
};

const laserFood: FoodDefinition = {
  id: FoodKind.Laser,
  label: "Purple – +1 length, adds 1 laser charge (tail)",
  color: 0x8e44ad,
  apply(model: GameModel): void {
    // Purple food grows the snake by 1 and tags the newly grown segment with a charge.
    model.snake.grow(1);
    model.tailModel.growAtTail({ skin: SegmentSkin.Purple, charge: new LaserCharge() });

    // Always level up (up to MAX_LASER_LEVEL) — not just the first time.
    const cur = model.snake.laserLevel ?? 0;
    model.snake.laserLevel = Math.min(MAX_LASER_LEVEL, cur + 1);
  },
};

const starFood: FoodDefinition = {
  id: FoodKind.Star,
  label: "Star – +75 pts, +1 length, adds Triple Beam charge (tail)",
  color: 0xffd347,
  apply(model: GameModel): void {
    model.snake.score += 75;
    model.snake.grow(1);
    model.tailModel.growAtTail({ skin: SegmentSkin.Star, charge: new TripleLaserCharge() });
  },
};

const icyFood: FoodDefinition = {
  id: FoodKind.Icy,
  label: "Icy – +60 pts, +1 length, adds Lightning charge (tail)",
  color: 0x4da3ff,
  apply(model: GameModel): void {
    model.snake.score += 60;
    model.snake.grow(1);
    model.tailModel.growAtTail({ skin: SegmentSkin.Icy, charge: new LightningCharge() });
  },
};

export const foodRegistry: Record<FoodKind, FoodDefinition> = {
  [FoodKind.Normal]: normalFood,
  [FoodKind.Gold]: goldFood,
  [FoodKind.Blue]: blueFood,
  [FoodKind.Laser]: laserFood,
  [FoodKind.Star]: starFood,
  [FoodKind.Icy]: icyFood,
};
